import React, { useState, useEffect } from 'react';
import { friendshipService } from '../services/api';
import './AddFriendButton.css';
import toast from 'react-hot-toast';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { Modal, Button } from 'react-bootstrap';

interface AddFriendButtonProps {
  targetUserId: string;
  currentUserId: string;
  onFriendshipStatusChange?: () => void;
}

type FriendshipStatus = 'none' | 'pending_sent' | 'pending_received' | 'accepted' | 'rejected';

const AddFriendButton: React.FC<AddFriendButtonProps> = ({ 
  targetUserId, 
  currentUserId, 
  onFriendshipStatusChange 
}) => {
  const { t } = useSafeTranslation();
  const [friendshipStatus, setFriendshipStatus] = useState<FriendshipStatus>('none');
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [friendshipId, setFriendshipId] = useState<number | null>(null);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkFriendshipStatus();
    }
  }, [targetUserId, currentUserId, isAuthenticated]);

  const checkAuthentication = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }
    setIsAuthenticated(true);
  };

  const checkFriendshipStatus = async () => {
    if (!isAuthenticated) {
      setFriendshipStatus('none');
      setIsChecking(false);
      return;
    }

    if (targetUserId === currentUserId) {
      setFriendshipStatus('none');
      setIsChecking(false);
      return;
    }

    try {
      setIsChecking(true);
      
      // Get friendship status from backend
      const statusResponse = await friendshipService.getFriendshipStatus(targetUserId);
      
      // Store the friendship ID if it exists
      setFriendshipId(statusResponse.friendshipId || null);
      
      // Map backend status to frontend status
      switch (statusResponse.status) {
        case 'None':
          setFriendshipStatus('none');
          break;
        case 'Pending':
          // Check if current user is the requester or receiver
          // Ensure both IDs are strings for comparison
          if (String(statusResponse.requesterId) === String(currentUserId)) {
            setFriendshipStatus('pending_sent');
          } else {
            setFriendshipStatus('pending_received');
          }
          break;
        case 'Accepted':
          setFriendshipStatus('accepted');
          break;
        case 'Rejected':
          setFriendshipStatus('rejected');
          break;
        default:
          setFriendshipStatus('none');
      }
    } catch {
      setFriendshipStatus('none');
    } finally {
      setIsChecking(false);
    }
  };

  const handleSendRequest = async () => {
    if (!isAuthenticated) {
      toast.error(t('friends.loginRequiredToSend', 'You need to be logged in to send friend requests.'));
      return;
    }

    try {
      setLoading(true);
      await friendshipService.sendFriendshipRequest(targetUserId);
      // Refresh the friendship status to get the updated state
      await checkFriendshipStatus();
      onFriendshipStatusChange?.();
      toast.success(t('friends.requestSent', 'Friend request sent successfully!'));
    } catch (err: any) {
      const message = err.response?.data?.message || t('friends.errorSendingRequest', 'Error sending friend request');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    setShowRemoveConfirmModal(true);
  };

  const confirmRemoveFriend = async () => {
    try {
      setLoading(true);
      await friendshipService.removeFriend(targetUserId);
      setFriendshipStatus('none');
      onFriendshipStatusChange?.();
      toast.success(t('friends.friendRemoved', 'Friend removed successfully!'));
    } catch (err: any) {
      const message = err.response?.data?.message || t('friends.removeFriend', 'Error removing friend');
      toast.error(message);
    } finally {
      setLoading(false);
      setShowRemoveConfirmModal(false);
    }
  };

  const cancelRemoveFriend = () => {
    setShowRemoveConfirmModal(false);
  };

  const handleAcceptRequest = async () => {
    if (!friendshipId) {
      toast.error(t('friends.requestNotFound', 'Friendship request not found.'));
      return;
    }

    try {
      setLoading(true);
      await friendshipService.respondToFriendshipRequest(friendshipId, true);
      // Refresh the friendship status to get the updated state
      await checkFriendshipStatus();
      onFriendshipStatusChange?.();
      toast.success(t('friends.requestAccepted', 'Friend request accepted!'));
    } catch (err: any) {
      const message = err.response?.data?.message || t('friends.errorAcceptingRequest', 'Error accepting friend request');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!friendshipId) {
      toast.error(t('friends.requestNotFound', 'Friendship request not found.'));
      return;
    }

    try {
      setLoading(true);
      await friendshipService.respondToFriendshipRequest(friendshipId, false);
      // Refresh the friendship status to get the updated state
      await checkFriendshipStatus();
      onFriendshipStatusChange?.();
      toast.success(t('friends.requestRejected', 'Friend request rejected.'));
    } catch (err: any) {
      const message = err.response?.data?.message || t('friends.errorRejectingRequest', 'Error rejecting friend request');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (isChecking) {
    return (
      <button className="add-friend-button loading" disabled>
        Checking...
      </button>
    );
  }

  if (!isAuthenticated) {
    return (
      <button className="add-friend-button not-authenticated" disabled>
        <span className="button-icon">🔒</span>
        {t('friends.loginToAddFriends', 'Login to add friends')}
      </button>
    );
  }

  if (targetUserId === currentUserId) {
    return null; // Don't show button for own profile
  }

  const renderButton = () => {
    switch (friendshipStatus) {
      case 'accepted':
        return (
          <div className="friendship-actions" key="accepted">
            <button className="add-friend-button accepted" disabled>
              <span className="button-icon">✓</span>
              {t('friends.friends', 'Friends')}
            </button>
            <button 
              className="add-friend-button remove-friend"
              onClick={handleRemoveFriend}
              disabled={loading}
            >
              <span className="button-icon">
                {loading ? '⏳' : '🗑️'}
              </span>
              {loading ? t('friends.removing', 'Removing...') : t('friends.removeFriend', 'Remove Friend')}
            </button>
          </div>
        );
      
      case 'pending_sent':
        return (
          <button className="add-friend-button pending" disabled key="pending_sent">
            <span className="button-icon">⏳</span>
            {t('friends.requestSent', 'Request Sent')}
          </button>
        );
      
      case 'pending_received':
        return (
          <div className="friendship-actions" key="pending_received">
            <button 
              className="add-friend-button accept-request"
              onClick={handleAcceptRequest}
              disabled={loading}
            >
              <span className="button-icon">
                {loading ? '⏳' : '✓'}
              </span>
              {loading ? t('friends.accepting', 'Accepting...') : t('friends.acceptRequest', 'Accept Request')}
            </button>
            <button 
              className="add-friend-button reject-request"
              onClick={handleRejectRequest}
              disabled={loading}
            >
              <span className="button-icon">
                {loading ? '⏳' : '✗'}
              </span>
              {loading ? t('friends.rejecting', 'Rejecting...') : t('friends.rejectRequest', 'Reject Request')}
            </button>
          </div>
        );
      
      case 'rejected':
        return (
          <button className="add-friend-button rejected" disabled key="rejected">
            <span className="button-icon">✗</span>
            {t('friends.requestRejected', 'Request Rejected')}
          </button>
        );
      
      default:
        return (
          <button 
            className="add-friend-button send-request"
            onClick={handleSendRequest}
            disabled={loading}
            key="none"
          >
            <span className="button-icon">
              {loading ? '⏳' : '👥'}
            </span>
            {loading ? t('friends.sending', 'Sending...') : t('friends.addFriend', 'Add Friend')}
          </button>
        );
    }
  };

  return (
    <>
      {renderButton()}

      {/* Remove Friend Confirmation Modal */}
      <Modal show={showRemoveConfirmModal} onHide={cancelRemoveFriend} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('friends.removeFriendModalTitle', 'Remove Friend')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t('friends.removeFriendModalMessage', 'Are you sure you want to remove {{name}} from your friends list? This action cannot be undone.')
              .replace('{{name}}', 'this friend')}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelRemoveFriend}>
            {t('friends.removeFriendCancelBtn', 'Cancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmRemoveFriend}
            disabled={loading}
          >
            {loading ? t('common.loading', 'Loading...') : t('friends.removeFriendConfirmBtn', 'Remove Friend')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddFriendButton; 